import { useState, useEffect, useRef, useCallback } from "react";
import { Chess } from "chess.js";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { MoveHistory } from "./MoveHistory";
import { 
  RotateCcw, 
  Download, 
  Trash2, 
  Clock,
  Crown,
  Zap,
  Send,
  Bot,
  User,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  Swords,
  TrendingUp,
  AlertTriangle,
  CheckCircle2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Piece symbols
const PIECES = {
  wK: "\u2654", wQ: "\u2655", wR: "\u2656", wB: "\u2657", wN: "\u2658", wP: "\u2659",
  bK: "\u265A", bQ: "\u265B", bR: "\u265C", bB: "\u265D", bN: "\u265E", bP: "\u265F",
};

const getPieceSymbol = (piece) => {
  if (!piece) return null;
  return PIECES[piece.color + piece.type.toUpperCase()];
};

const UCI_MOVE_REGEX = /\b([a-h][1-8][a-h][1-8][qrbn]?)\b/gi;
const PREVIEW_CODE_LINE_REGEX = /^\s*(PREVIEW_CODE|REPLY_PREVIEW_CODE)\s*:\s*([a-h][1-8][a-h][1-8][qrbn]?)\s*$/i;
const MOVE_LINE_REGEX = /^(\s*(?:MOVE|OPPONENT_MOVE|REPLY_MOVE)\s*:\s*)(.+?)\s*$/i;

const attachPreviewLinksAndHideCodes = (text) => {
  if (!text) return "";
  const lines = String(text).split(/\r?\n/);
  const out = [];
  let lastMoveLineIndex = -1;

  for (const line of lines) {
    const previewMatch = line.match(PREVIEW_CODE_LINE_REGEX);
    if (previewMatch) {
      const uci = previewMatch[2].toLowerCase();
      if (lastMoveLineIndex >= 0) {
        const moveLine = out[lastMoveLineIndex];
        const moveMatch = typeof moveLine === "string" ? moveLine.match(MOVE_LINE_REGEX) : null;
        if (moveMatch) {
          const prefix = moveMatch[1];
          const label = moveMatch[2].trim();
          out[lastMoveLineIndex] = `${prefix}[${label}](#move-${uci})`;
        }
      }
      continue; // hide machine-only code lines from the UI
    }

    out.push(line);
    if (MOVE_LINE_REGEX.test(line)) {
      lastMoveLineIndex = out.length - 1;
    } else if (line.trim() !== "") {
      lastMoveLineIndex = -1;
    }
  }

  return out.join("\n");
};

const decorateCoachMarkdown = (text, fen) => {
  if (!text) return "";
  const displayText = attachPreviewLinksAndHideCodes(text);
  let board = null;
  try {
    board = new Chess(fen || undefined);
  } catch {
    board = null;
  }

  return displayText.replace(UCI_MOVE_REGEX, (_, rawMove) => {
    const move = String(rawMove).toLowerCase();
    let label = move;
    if (board) {
      try {
        const parsed = board.move({
          from: move.slice(0, 2),
          to: move.slice(2, 4),
          promotion: move[4] || "q",
        });
        if (parsed?.san) {
          label = parsed.san;
        }
        board.undo();
      } catch {
        // Keep UCI label if move is not legal in the current position.
      }
    }
    return `[${label}](#move-${move})`;
  });
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const START_FILES = ["a", "b", "c", "d", "e", "f", "g", "h"];

const createInitialPieceMap = () => {
  const map = {};
  let counter = 0;
  const addPiece = (square, color, type) => {
    map[square] = { id: `${color}-${type}-${counter++}`, color, type, square };
  };

  for (const file of START_FILES) {
    addPiece(`${file}2`, "w", "p");
    addPiece(`${file}7`, "b", "p");
  }

  [
    ["a1", "w", "r"], ["b1", "w", "n"], ["c1", "w", "b"], ["d1", "w", "q"],
    ["e1", "w", "k"], ["f1", "w", "b"], ["g1", "w", "n"], ["h1", "w", "r"],
    ["a8", "b", "r"], ["b8", "b", "n"], ["c8", "b", "b"], ["d8", "b", "q"],
    ["e8", "b", "k"], ["f8", "b", "b"], ["g8", "b", "n"], ["h8", "b", "r"],
  ].forEach(([square, color, type]) => addPiece(square, color, type));

  return map;
};

const applyTrackedMove = (board, pieceMap, uciMove) => {
  const move = board.move({
    from: uciMove.slice(0, 2),
    to: uciMove.slice(2, 4),
    promotion: uciMove[4] || "q",
  });
  if (!move) {
    return;
  }

  const movingPiece = pieceMap[move.from];
  if (!movingPiece) {
    return;
  }

  delete pieceMap[move.from];

  if (move.flags.includes("e")) {
    const captureRank = move.color === "w" ? Number(move.to[1]) - 1 : Number(move.to[1]) + 1;
    delete pieceMap[`${move.to[0]}${captureRank}`];
  } else if (pieceMap[move.to]) {
    delete pieceMap[move.to];
  }

  if (move.flags.includes("k") || move.flags.includes("q")) {
    const rookFrom = move.flags.includes("k")
      ? `${"h"}${move.color === "w" ? "1" : "8"}`
      : `${"a"}${move.color === "w" ? "1" : "8"}`;
    const rookTo = move.flags.includes("k")
      ? `${"f"}${move.color === "w" ? "1" : "8"}`
      : `${"d"}${move.color === "w" ? "1" : "8"}`;
    if (pieceMap[rookFrom]) {
      pieceMap[rookTo] = { ...pieceMap[rookFrom], square: rookTo };
      delete pieceMap[rookFrom];
    }
  }

  pieceMap[move.to] = {
    ...movingPiece,
    square: move.to,
    type: move.promotion || movingPiece.type,
  };
};

const buildTrackedPieceMap = (moves, previewMove = null) => {
  const board = new Chess();
  const pieceMap = createInitialPieceMap();

  for (const move of moves || []) {
    if (!/^[a-h][1-8][a-h][1-8][qrbn]?$/i.test(move)) {
      continue;
    }
    applyTrackedMove(board, pieceMap, move.toLowerCase());
  }

  if (previewMove && /^[a-h][1-8][a-h][1-8][qrbn]?$/i.test(previewMove)) {
    try {
      applyTrackedMove(board, pieceMap, previewMove.toLowerCase());
    } catch {
      // Ignore preview animation if the move is invalid in the current position.
    }
  }

  return pieceMap;
};

const describeAdvantage = (evalCp) => {
  if (evalCp >= 600) return "White is winning by engine evaluation";
  if (evalCp >= 250) return "White has a large edge";
  if (evalCp >= 100) return "White is clearly better";
  if (evalCp >= 35) return "White is slightly better";
  if (evalCp > -35) return "The position is roughly balanced";
  if (evalCp > -100) return "Black is slightly better";
  if (evalCp > -250) return "Black is clearly better";
  if (evalCp > -600) return "Black has a large edge";
  return "Black is winning by engine evaluation";
};

const EvaluationBar = ({ whiteEvalCp, whiteWdl, narrative }) => {
  const evalCp = Number.isFinite(whiteEvalCp) ? whiteEvalCp : 0;
  const whiteWinPct = Number(whiteWdl?.white_win_pct ?? 0);
  const drawPct = Number(whiteWdl?.draw_pct ?? 100);
  const blackWinPct = Number(whiteWdl?.black_win_pct ?? 0);
  const expectedScorePct = clamp(Number(whiteWdl?.expected_score_pct ?? 50), 2, 98);
  const swingPawns = narrative?.last_move?.delta_cp
    ? (Math.abs(narrative.last_move.delta_cp) / 100).toFixed(2)
    : null;
  const swingOwner = narrative?.last_move
    ? narrative.last_move.mover_color === "white"
      ? "White"
      : "Black"
    : null;
  const advantageText = describeAdvantage(evalCp);
  const sideLabel = evalCp > 0 ? "White edge" : evalCp < 0 ? "Black edge" : "Equal";

  return (
    <div className="mb-3 w-full max-w-[560px] rounded-sm border border-white/10 bg-white/[0.03] px-3 py-3 sm:px-4">
      <div className="flex items-center justify-between gap-3 text-[11px] uppercase tracking-[0.2em] text-white/45">
        <span>White</span>
        <span>Engine WDL</span>
        <span>Black</span>
      </div>
      <div className="mt-3">
        <div className="relative h-4 overflow-hidden rounded-full border border-white/10 bg-black/40">
          <div className="absolute inset-y-0 left-0 bg-[#F4F0E6]" style={{ width: `${whiteWinPct}%` }} />
          <div className="absolute inset-y-0 bg-white/30" style={{ left: `${whiteWinPct}%`, width: `${drawPct}%` }} />
          <div className="absolute inset-y-0 right-0 bg-[#111111]" style={{ width: `${blackWinPct}%` }} />
          <div className="absolute inset-y-[-4px] w-1.5 rounded-full border border-amber-300 bg-amber-400 shadow-[0_0_14px_rgba(250,204,21,0.45)]" style={{ left: `calc(${expectedScorePct}% - 3px)` }} />
        </div>
        <div className="mt-2 grid grid-cols-3 gap-2 text-[11px] font-mono">
          <span className="text-slate-200">W {whiteWinPct.toFixed(1)}%</span>
          <span className="text-center text-white/60">D {drawPct.toFixed(1)}%</span>
          <span className="text-right text-white">B {blackWinPct.toFixed(1)}%</span>
        </div>
        <div className="mt-2 flex items-center justify-between text-[11px] font-mono">
          <span className="text-slate-200">White expected score</span>
          <span className="text-white/45">{evalCp >= 0 ? `+${(evalCp / 100).toFixed(2)}` : `${(evalCp / 100).toFixed(2)}`}</span>
          <span className="text-white">{expectedScorePct.toFixed(1)}%</span>
        </div>
        <p className="mt-2 text-[11px] text-white/55">
          <span className="font-semibold text-white/80">{advantageText}</span>. These percentages are engine-derived win/draw/loss estimates, not certainties.
        </p>
        <p className="mt-1 text-[11px] text-white/35">
          White segment = White wins, middle = draws, Black segment = Black wins. Marker = White expected score. Current read: <span className="font-mono text-white/60">{sideLabel}</span>.
        </p>
        {swingPawns ? (
          <p className="mt-2 text-[11px] text-white/50">
            Last move swing: <span className="font-mono text-amber-300">{swingPawns} pawns</span> against {swingOwner}.
          </p>
        ) : (
          <p className="mt-2 text-[11px] text-white/35">
            The marker shifts after every move based on the current Stockfish evaluation.
          </p>
        )}
      </div>
    </div>
  );
};

// Chess Board Component
const ChessBoard = ({ position, orientation, selectedSquare, legalMoves, onSquareClick, lastMove, suggestedMove, previewMove, renderMoves }) => {
  const files = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'];
  const ranks = ['8', '7', '6', '5', '4', '3', '2', '1'];
  
  const displayFiles = orientation === 'black' ? [...files].reverse() : files;
  const displayRanks = orientation === 'black' ? [...ranks].reverse() : ranks;

  const chess = new Chess(position);
  const trackedPieces = buildTrackedPieceMap(renderMoves, previewMove);

  return (
    <LayoutGroup id="chess-board-layout">
      <div className="grid grid-cols-8 w-full max-w-[480px] aspect-square border-2 border-white/10 rounded overflow-hidden" data-testid="chess-board">
        {displayRanks.map((rank, rankIdx) => (
          displayFiles.map((file, fileIdx) => {
            const square = file + rank;
            const isLight = (rankIdx + fileIdx) % 2 === 0;
            const piece = chess.get(square);
            const trackedPiece = trackedPieces[square];
            const isSelected = selectedSquare === square;
            const isLegalMove = legalMoves.includes(square);
            const isLastMove = lastMove && (lastMove.from === square || lastMove.to === square);
            const isSuggestedFrom = suggestedMove && suggestedMove.slice(0, 2) === square;
            const isSuggestedTo = suggestedMove && suggestedMove.slice(2, 4) === square;

            let bgClass = isLight ? 'bg-[#E8E0D0]' : 'bg-[#B58863]';
            if (isLastMove) bgClass = 'bg-yellow-500/50';
            if (isSuggestedFrom || isSuggestedTo) bgClass = 'bg-emerald-500/50';

            return (
              <div
                key={square}
                data-square={square}
                onClick={() => onSquareClick(square)}
                className={`
                  relative flex items-center justify-center cursor-pointer
                  text-4xl select-none transition-all duration-150
                  ${bgClass}
                  ${isSelected ? 'ring-4 ring-inset ring-amber-400' : ''}
                  hover:brightness-110
                `}
                style={{ aspectRatio: '1' }}
              >
                {isLegalMove && !piece && (
                  <div className="absolute w-4 h-4 rounded-full bg-black/25" />
                )}
                {isLegalMove && piece && (
                  <div className="absolute inset-1 ring-4 ring-black/25 rounded-full" />
                )}

                {trackedPiece && (
                  <motion.span
                    layoutId={trackedPiece.id}
                    transition={{ type: "spring", stiffness: 420, damping: 32, mass: 0.8 }}
                    className={`absolute inset-0 flex items-center justify-center ${trackedPiece.color === 'w' ? 'text-white drop-shadow-[0_2px_3px_rgba(0,0,0,0.9)]' : 'text-gray-900'}`}
                  >
                    {getPieceSymbol(trackedPiece)}
                  </motion.span>
                )}

                {isSuggestedTo && !piece && (
                  <div className="absolute w-8 h-8 border-4 border-emerald-400 rounded-full animate-pulse" />
                )}

                {fileIdx === 0 && (
                  <span className={`absolute top-0.5 left-1 text-[10px] font-bold ${isLight ? 'text-[#B58863]' : 'text-[#E8E0D0]'}`}>
                    {rank}
                  </span>
                )}
                {rankIdx === 7 && (
                  <span className={`absolute bottom-0.5 right-1 text-[10px] font-bold ${isLight ? 'text-[#B58863]' : 'text-[#E8E0D0]'}`}>
                    {file}
                  </span>
                )}
              </div>
            );
          })
        ))}
      </div>
    </LayoutGroup>
  );
};

export const ChessGame = ({ game, settings, onGameUpdate, apiClient }) => {
  const chessRef = useRef(new Chess(game.fen));
  const gameRef = useRef(game); // Track latest game state
  const [position, setPosition] = useState(game.fen);
  const [selectedSquare, setSelectedSquare] = useState(null);
  const [legalMoves, setLegalMoves] = useState([]);
  const [lastMove, setLastMove] = useState(null);
  const [reviewMoveIndex, setReviewMoveIndex] = useState(null);
  const [reviewPosition, setReviewPosition] = useState(null);
  const [reviewLastMove, setReviewLastMove] = useState(null);
  const [suggestedMove, setSuggestedMove] = useState(null);
  const [hoveredCoachMove, setHoveredCoachMove] = useState(null);
  const [isThinking, setIsThinking] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const lastGameIdRef = useRef(game.id);
  
  // Always keep gameRef in sync with latest game prop
  useEffect(() => {
    gameRef.current = game;
  }, [game]);
  
  // Coach state
  const [chatMessages, setChatMessages] = useState(game.chat_history || []);
  const [chatInput, setChatInput] = useState("");
  const [engineMeta, setEngineMeta] = useState({ name: 'Stockfish', depth: settings?.engine_depth || 18, available: true });
  const [bestMoveData, setBestMoveData] = useState(null);
  const [topLines, setTopLines] = useState([]);
  const [narrative, setNarrative] = useState(null);
  const [whiteEvalCp, setWhiteEvalCp] = useState(0);
  const [whiteWdl, setWhiteWdl] = useState(null);
  const [engineUnavailable, setEngineUnavailable] = useState(false);
  const chatViewportRef = useRef(null);
  const chatBottomRef = useRef(null);
  const pendingHudKeyRef = useRef(null);
  const applyHudSnapshot = useCallback((snapshot) => {
    setSuggestedMove(snapshot?.suggested_move || null);
    setBestMoveData(snapshot?.best_move || null);
    setTopLines(Array.isArray(snapshot?.top_lines) ? snapshot.top_lines : []);
    setNarrative(snapshot?.narrative || null);
    setWhiteEvalCp(Number.isFinite(snapshot?.white_eval_cp) ? snapshot.white_eval_cp : 0);
    setWhiteWdl(snapshot?.white_wdl || null);
  }, []);

  const clearReviewMode = () => {
    setReviewMoveIndex(null);
    setReviewPosition(null);
    setReviewLastMove(null);
    setSelectedSquare(null);
    setLegalMoves([]);
  };

  // Full reset ONLY when switching to a different game
  useEffect(() => {
    if (lastGameIdRef.current !== game.id) {
      lastGameIdRef.current = game.id;
      const fen = game.fen || "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1";
      chessRef.current = new Chess(fen);
      setPosition(fen);
      clearReviewMode();
      setSuggestedMove(null);
      setHoveredCoachMove(null);
      setChatMessages(game.chat_history || []);
      applyHudSnapshot((game.hud_history || [])[game.moves.length - 1] || null);
      
      if (game.moves.length > 0) {
        const lastMoveUci = game.moves[game.moves.length - 1];
        setLastMove({ from: lastMoveUci.slice(0, 2), to: lastMoveUci.slice(2, 4) });
      } else {
        setLastMove(null);
      }
    }
  }, [applyHudSnapshot, game.id, game.fen, game.moves, game.chat_history, game.total_tokens, game.total_cost, game.hud_history]);

  // Sync chat history when it changes (without resetting board)
  useEffect(() => {
    setChatMessages(game.chat_history || []);
  }, [game.chat_history]);

  useEffect(() => {
    if (reviewMoveIndex !== null) {
      return;
    }
    applyHudSnapshot((game.hud_history || [])[game.moves.length - 1] || null);
  }, [applyHudSnapshot, game.hud_history, game.moves.length, reviewMoveIndex]);

  // Auto-scroll chat to the latest entry, including on initial page load.
  useEffect(() => {
    const scrollToBottom = () => {
      if (chatBottomRef.current) {
        chatBottomRef.current.scrollIntoView({ block: "end" });
        return;
      }
      if (chatViewportRef.current) {
        chatViewportRef.current.scrollTop = chatViewportRef.current.scrollHeight;
      }
    };
    const frame = requestAnimationFrame(scrollToBottom);
    return () => cancelAnimationFrame(frame);
  }, [game.id, chatMessages]);

  // Engine-backed request. The backend refuses to answer if Stockfish is unavailable.
  const askCoach = useCallback(async (userMessage, isAutoSuggest = false) => {
    setIsThinking(true);

    try {
      const data = await apiClient.coach(game.id, {
        game_id: game.id,
        message: userMessage,
        provider: 'engine',
        model: null,
        is_auto_suggest: isAutoSuggest,
      });

      if (data.success) {
        setEngineUnavailable(false);
        if (data.engine) {
          setEngineMeta({
            name: data.engine.name || 'Stockfish',
            depth: data.engine.depth || settings?.engine_depth || 18,
            available: data.engine.available !== false,
          });
        }
        setChatMessages(data.chat_history || []);
        setSuggestedMove(data.suggested_move || null);
        setBestMoveData(data.best_move || null);
        setTopLines(Array.isArray(data.top_lines) ? data.top_lines : []);
        setNarrative(data.narrative || null);
        setWhiteEvalCp(Number.isFinite(data.white_eval_cp) ? data.white_eval_cp : 0);
        setWhiteWdl(data.white_wdl || null);
        const latestGame = gameRef.current;
        const updatedGame = {
          ...latestGame,
          suggested_move: data.suggested_move || null,
          chat_history: data.chat_history || [],
          hud_history: Array.isArray(data.hud_history) ? data.hud_history : (latestGame.hud_history || []),
        };
        gameRef.current = updatedGame;
        onGameUpdate(updatedGame);
        return data;
      }
    } catch (error) {
      console.error("Engine coach error:", error);
      const detail = error.response?.data?.detail || 'Engine unavailable';
      if (detail.toLowerCase().includes('stockfish') || detail.toLowerCase().includes('engine')) {
        setEngineUnavailable(true);
      }
      if (!isAutoSuggest) {
        toast.error(detail);
      }
    } finally {
      setIsThinking(false);
    }
    return null;
  }, [apiClient, game.id, onGameUpdate, settings?.engine_depth]);

  useEffect(() => {
    if (reviewMoveIndex !== null || !apiClient?.getHud || game.status !== 'active' || !game.is_player_turn) {
      return;
    }
    const latestIndex = game.moves.length - 1;
    if (latestIndex < 0 || (game.hud_history || [])[latestIndex]) {
      return;
    }

    const requestKey = `${game.id}:${latestIndex}`;
    if (pendingHudKeyRef.current === requestKey) {
      return;
    }

    let cancelled = false;
    pendingHudKeyRef.current = requestKey;
    setIsThinking(true);

    apiClient.getHud(game.id, latestIndex)
      .then((data) => {
        if (cancelled || !data?.success) {
          return;
        }
        setEngineUnavailable(false);
        if (data.engine) {
          setEngineMeta({
            name: data.engine.name || 'Stockfish',
            depth: data.engine.depth || settings?.engine_depth || 18,
            available: data.engine.available !== false,
          });
        }
        const latestGame = gameRef.current;
        const updatedGame = {
          ...latestGame,
          hud_history: Array.isArray(data.hud_history) ? data.hud_history : (latestGame.hud_history || []),
          suggested_move: data.snapshot?.suggested_move || latestGame.suggested_move || null,
        };
        gameRef.current = updatedGame;
        onGameUpdate(updatedGame);
        applyHudSnapshot(data.snapshot || null);
      })
      .catch((error) => {
        if (cancelled) {
          return;
        }
        const detail = error.response?.data?.detail || 'Engine unavailable';
        if (detail.toLowerCase().includes('stockfish') || detail.toLowerCase().includes('engine')) {
          setEngineUnavailable(true);
        }
      })
      .finally(() => {
        if (!cancelled && pendingHudKeyRef.current === requestKey) {
          pendingHudKeyRef.current = null;
        }
        if (!cancelled) {
          setIsThinking(false);
        }
      });

    return () => {
      cancelled = true;
      if (pendingHudKeyRef.current === requestKey) {
        pendingHudKeyRef.current = null;
      }
    };
  }, [apiClient, applyHudSnapshot, game.hud_history, game.id, game.is_player_turn, game.moves.length, game.status, onGameUpdate, reviewMoveIndex, settings?.engine_depth]);

  const getPositionAtMoveIndex = (moves, targetIndex) => {
    const reviewChess = new Chess();
    if (targetIndex == null || targetIndex < 0) {
      return { fen: reviewChess.fen(), lastMove: null };
    }

    for (let i = 0; i <= targetIndex && i < moves.length; i += 1) {
      const move = moves[i];
      reviewChess.move({
        from: move.slice(0, 2),
        to: move.slice(2, 4),
        promotion: move[4] || 'q',
      });
    }

    const move = moves[targetIndex];
    return {
      fen: reviewChess.fen(),
      lastMove: move ? { from: move.slice(0, 2), to: move.slice(2, 4) } : null,
    };
  };

  const handleMoveHistorySelect = (moveIndex) => {
    if (moveIndex == null || moveIndex >= game.moves.length - 1) {
      clearReviewMode();
      return;
    }

    const snapshot = getPositionAtMoveIndex(game.moves, moveIndex);
    setReviewMoveIndex(moveIndex);
    setReviewPosition(snapshot.fen);
    setReviewLastMove(snapshot.lastMove);
    setSelectedSquare(null);
    setLegalMoves([]);

    if ((game.hud_history || [])[moveIndex] || !apiClient?.getHud) {
      return;
    }

    const requestKey = `${game.id}:${moveIndex}`;
    if (pendingHudKeyRef.current === requestKey) {
      return;
    }
    pendingHudKeyRef.current = requestKey;
    setIsThinking(true);
    apiClient.getHud(game.id, moveIndex)
      .then((data) => {
        if (!data?.success) {
          return;
        }
        const latestGame = gameRef.current;
        const updatedGame = {
          ...latestGame,
          hud_history: Array.isArray(data.hud_history) ? data.hud_history : (latestGame.hud_history || []),
        };
        gameRef.current = updatedGame;
        onGameUpdate(updatedGame);
      })
      .catch((error) => {
        console.error("HUD history load error:", error);
      })
      .finally(() => {
        if (pendingHudKeyRef.current === requestKey) {
          pendingHudKeyRef.current = null;
        }
        setIsThinking(false);
      });
  };

  const handleStepBackward = () => {
    if (!game.moves.length) {
      return;
    }
    const targetIndex = reviewMoveIndex == null ? game.moves.length - 2 : reviewMoveIndex - 1;
    if (targetIndex < 0) {
      return;
    }
    handleMoveHistorySelect(targetIndex);
  };

  const handleStepForward = () => {
    if (!game.moves.length || reviewMoveIndex == null) {
      return;
    }
    const targetIndex = reviewMoveIndex + 1;
    if (targetIndex >= game.moves.length - 1) {
      clearReviewMode();
      return;
    }
    handleMoveHistorySelect(targetIndex);
  };

  // Handle square click
  const handleSquareClick = async (square) => {
    if (reviewMoveIndex !== null) {
      toast.info("Viewing history. Click the latest move to return to the live board.");
      return;
    }

    const chess = chessRef.current;
    
    // If piece selected and clicking legal move
    if (selectedSquare && legalMoves.includes(square)) {
      try {
        const result = chess.move({ from: selectedSquare, to: square, promotion: 'q' });
        if (result) {
          const newPosition = chess.fen();
          setPosition(newPosition);
          clearReviewMode();
          setLastMove({ from: selectedSquare, to: square });
          setSuggestedMove(null); // Clear suggestion after any move
          setHoveredCoachMove(null);
          
          const move = `${selectedSquare}${square}`;
          const currentGame = gameRef.current; // Use ref for latest state
          
          const response = await apiClient.move(currentGame.id, {
            game_id: currentGame.id,
            move: move
          });
          
          if (response.success) {
            // Build updated game using response data + latest ref state
            const latestGame = gameRef.current;
            const updatedGame = {
              ...latestGame,
              fen: response.fen,
              moves: [...latestGame.moves, response.move],
              is_player_turn: response.is_player_turn,
              status: response.status,
              pgn: response.pgn,
              hud_history: Array.isArray(response.hud_history) ? response.hud_history : (latestGame.hud_history || []),
              suggested_move: Array.isArray(response.hud_history)
                ? response.hud_history[response.hud_history.length - 1]?.suggested_move || null
                : null,
            };
            
            // Update ref immediately before calling onGameUpdate
            gameRef.current = updatedGame;
            onGameUpdate(updatedGame);
            
            if (response.is_check) toast.warning("Check!");
            if (response.status === 'checkmate') toast.success("Checkmate!");
            
          }
        }
      } catch (error) {
        console.error("Move error:", error);
        chess.undo();
        setPosition(chess.fen());
        toast.error(error.response?.data?.detail || "Invalid move");
      }
      
      setSelectedSquare(null);
      setLegalMoves([]);
      return;
    }
    
    // Deselect if clicking same square
    if (selectedSquare === square) {
      setSelectedSquare(null);
      setLegalMoves([]);
      return;
    }
    
    // Select piece
    const piece = chess.get(square);
    if (piece) {
      setSelectedSquare(square);
      const moves = chess.moves({ square, verbose: true });
      setLegalMoves(moves.map(m => m.to));
    } else {
      setSelectedSquare(null);
      setLegalMoves([]);
    }
  };

  // Handle chat send
  const handleChatSend = async () => {
    if (!chatInput.trim() || isThinking) return;
    const message = chatInput.trim();
    setChatInput("");
    await askCoach(message, false);
  };

  const handleRefreshBestMove = async () => {
    const targetIndex = reviewMoveIndex ?? (game.moves.length - 1);
    if (!apiClient?.getHud || targetIndex < 0) {
      return;
    }
    setIsThinking(true);
    try {
      const data = await apiClient.getHud(game.id, targetIndex);
      if (data?.success) {
        const latestGame = gameRef.current;
        const updatedGame = {
          ...latestGame,
          hud_history: Array.isArray(data.hud_history) ? data.hud_history : (latestGame.hud_history || []),
          suggested_move: data.snapshot?.suggested_move || latestGame.suggested_move || null,
        };
        gameRef.current = updatedGame;
        onGameUpdate(updatedGame);
        if (reviewMoveIndex === null) {
          applyHudSnapshot(data.snapshot || null);
        }
      }
    } finally {
      setIsThinking(false);
    }
  };

  const handleShowTopLines = async () => {
    await handleRefreshBestMove();
  };

  // Undo move
  const handleUndo = async () => {
    try {
      const currentGame = gameRef.current;
      const response = await apiClient.undo(currentGame.id);
      if (response.success) {
        const updatedMoves = currentGame.moves.slice(0, -1);
        const updatedHudHistory = Array.isArray(response.hud_history)
          ? response.hud_history
          : (currentGame.hud_history || []).slice(0, -1);
        chessRef.current = new Chess(response.fen);
        setPosition(response.fen);
        clearReviewMode();
        setHoveredCoachMove(null);
        applyHudSnapshot(updatedHudHistory[updatedMoves.length - 1] || null);
        setLastMove(
          updatedMoves.length
            ? {
                from: updatedMoves[updatedMoves.length - 1].slice(0, 2),
                to: updatedMoves[updatedMoves.length - 1].slice(2, 4),
              }
            : null
        );

        const updatedGame = {
          ...currentGame,
          fen: response.fen,
          moves: updatedMoves,
          is_player_turn: response.is_player_turn,
          status: 'active',
          hud_history: updatedHudHistory,
          suggested_move: updatedHudHistory[updatedMoves.length - 1]?.suggested_move || null,
        };
        gameRef.current = updatedGame;
        onGameUpdate(updatedGame);
        toast.success("Move undone");
      }
    } catch (error) {
      toast.error("Cannot undo");
    }
  };

  // Export PGN
  const handleExportPGN = async () => {
    try {
      const response = await apiClient.exportPgn(game.id);
      const blob = new Blob([response.pgn], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `game_${game.id.slice(0, 8)}.pgn`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PGN exported");
    } catch (error) {
      toast.error("Export failed");
    }
  };

  // Delete game
  const handleDelete = async () => {
    try {
      await apiClient.deleteGame(game.id);
      localStorage.removeItem('chess_current_game_id');
      toast.success("Game deleted");
      window.location.reload();
    } catch (error) {
      toast.error("Delete failed");
    }
    setShowDeleteDialog(false);
  };

  const boardOrientation = game.player_color === 'black' ? 'black' : 'white';
  const coachEnabled = typeof apiClient?.coach === 'function' && !engineUnavailable;
  const isReviewingHistory = reviewMoveIndex !== null;
  const reviewHudSnapshot = isReviewingHistory ? (game.hud_history || [])[reviewMoveIndex] || null : null;
  const displayedSuggestedMove = isReviewingHistory ? reviewHudSnapshot?.suggested_move || null : suggestedMove;
  const displayedBestMoveData = isReviewingHistory ? reviewHudSnapshot?.best_move || null : bestMoveData;
  const displayedTopLines = isReviewingHistory ? (Array.isArray(reviewHudSnapshot?.top_lines) ? reviewHudSnapshot.top_lines : []) : topLines;
  const displayedNarrative = isReviewingHistory ? reviewHudSnapshot?.narrative || null : narrative;
  const displayedWhiteEvalCp = isReviewingHistory
    ? (Number.isFinite(reviewHudSnapshot?.white_eval_cp) ? reviewHudSnapshot.white_eval_cp : 0)
    : whiteEvalCp;
  const displayedWhiteWdl = isReviewingHistory ? reviewHudSnapshot?.white_wdl || null : whiteWdl;
  const displayedPosition = reviewPosition || position;
  const displayedLastMove = reviewLastMove || lastMove;
  const displayedMoveIndex = isReviewingHistory ? reviewMoveIndex : game.moves.length - 1;
  const displayedMoves = isReviewingHistory ? game.moves.slice(0, reviewMoveIndex + 1) : game.moves;
  const canStepBackward = game.moves.length >= 2 && (isReviewingHistory ? reviewMoveIndex > 0 : game.moves.length > 1);
  const canStepForward = isReviewingHistory;
  const engineDepth = engineMeta.depth || settings?.engine_depth || 18;
  const moodStyles = {
    opportunity: {
      icon: AlertTriangle,
      border: 'border-emerald-500/30',
      bg: 'bg-emerald-500/8',
      title: 'text-emerald-300',
    },
    warning: {
      icon: AlertTriangle,
      border: 'border-amber-500/30',
      bg: 'bg-amber-500/8',
      title: 'text-amber-300',
    },
    expected: {
      icon: CheckCircle2,
      border: 'border-sky-500/25',
      bg: 'bg-sky-500/8',
      title: 'text-sky-300',
    },
    steady: {
      icon: TrendingUp,
      border: 'border-white/10',
      bg: 'bg-white/[0.02]',
      title: 'text-white/80',
    },
  };
  const narrativeStyle = moodStyles[displayedNarrative?.mood] || moodStyles.steady;
  const NarrativeIcon = narrativeStyle.icon;

  return (
    <div className="flex min-h-[calc(100vh-80px)] flex-col lg:flex-row lg:h-[calc(100vh-80px)]" data-testid="chess-game-container">
      {/* Left - Move History */}
      <aside className="order-2 lg:order-1 w-full lg:w-48 border-t lg:border-t-0 lg:border-r border-white/5 bg-[#0A0A0A] max-h-64 lg:max-h-none overflow-y-auto">
        <MoveHistory 
          moves={game.moves} 
          playerColor={game.player_color}
          currentMoveIndex={displayedMoveIndex}
          onMoveSelect={handleMoveHistorySelect}
        />
      </aside>

      {/* Center - Board */}
      <div className="order-1 lg:order-2 flex-1 bg-[#050505] p-3 sm:p-4">
        <div className="mx-auto grid w-full max-w-[1180px] grid-cols-1 gap-3 lg:grid-cols-[280px_minmax(0,560px)_280px] lg:items-start">
          <div className="order-2 flex flex-col gap-3 lg:order-1">
            <div className="h-[196px] overflow-hidden rounded-md border border-white/8 bg-white/[0.02] p-3">
              <EvaluationBar whiteEvalCp={displayedWhiteEvalCp} whiteWdl={displayedWhiteWdl} narrative={displayedNarrative} />
            </div>

            <div className={`h-[248px] overflow-hidden rounded-md border p-3 ${narrativeStyle.border} ${narrativeStyle.bg}`}>
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/55">
                <NarrativeIcon size={12} className={narrativeStyle.title} />
                Match Read
              </div>
              {displayedNarrative ? (
                <div className="mt-3 h-[188px] space-y-2 overflow-y-auto">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className={`text-sm font-semibold ${narrativeStyle.title}`}>{displayedNarrative.headline}</span>
                    <span className="rounded-sm bg-black/20 px-2 py-1 font-mono text-[11px] text-white/65">
                      eval {displayedNarrative.eval}
                    </span>
                  </div>
                  <p className="text-xs leading-relaxed text-white/70">{displayedNarrative.summary}</p>
                  {displayedNarrative.last_move ? (
                    <div className="rounded border border-white/8 bg-black/20 px-3 py-2 text-[11px] text-white/55">
                      Last move: <span className="font-mono text-white/80">{displayedNarrative.last_move.played_san}</span>
                      {" | "}
                      <span className="font-semibold text-amber-300">{displayedNarrative.last_move.label}</span>
                      {" | "}
                      Engine preferred <span className="font-mono text-emerald-300">{displayedNarrative.last_move.best_san}</span>
                    </div>
                  ) : null}
                </div>
              ) : (
                <p className="mt-3 text-xs text-white/40">
                  The engine will summarize whether the game is going to plan once analysis is loaded.
                </p>
              )}
            </div>
          </div>

          <div className="order-1 flex flex-col gap-3 lg:order-2">
            <div className="flex min-h-[44px] w-full flex-wrap items-center gap-2 sm:gap-3">
              <div
                className={`px-3 sm:px-4 py-2 rounded-sm flex items-center gap-2 ${
                  game.is_player_turn
                    ? 'bg-amber-500/20 border border-amber-500/40'
                    : 'bg-sky-500/20 border border-sky-500/40'
                }`}
                data-testid="turn-indicator"
              >
                {game.is_player_turn ? (
                  <>
                    <Crown size={14} className="text-amber-400" />
                    <span className="text-[11px] sm:text-xs font-medium text-amber-400 uppercase tracking-wide">Your Move</span>
                  </>
                ) : (
                  <>
                    <Clock size={14} className="text-sky-400" />
                    <span className="text-[11px] sm:text-xs font-medium text-sky-400 uppercase tracking-wide">Opponent&apos;s Move</span>
                  </>
                )}
              </div>

              <div className="px-3 py-2 bg-white/5 border border-white/10 rounded-sm flex items-center gap-2 min-w-0" data-testid="engine-status">
                <Bot size={12} className="text-amber-400" />
                <span className="font-mono text-[10px] text-white/60 whitespace-nowrap">{engineMeta.name}</span>
                <span className="font-mono text-[10px] text-amber-400">d{engineDepth}</span>
              </div>

              {isReviewingHistory && (
                <button
                  type="button"
                  onClick={() => clearReviewMode()}
                  className="px-3 py-2 bg-white/5 border border-white/10 rounded-sm text-[10px] uppercase tracking-wide text-white/70 hover:bg-white/10"
                >
                  Reviewing move {reviewMoveIndex + 1} | Back to live
                </button>
              )}
            </div>

            <div className="flex h-5 w-full items-center gap-2 text-xs text-white/50">
              {isThinking ? (
                <>
                  <div className="w-3 h-3 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
                  Engine is analyzing...
                </>
              ) : (
                <span className="opacity-0">Engine is analyzing...</span>
              )}
            </div>

            <div className="flex h-[520px] w-full items-center justify-center rounded-md border border-white/8 bg-white/[0.02] p-3">
              <ChessBoard
                position={displayedPosition}
                orientation={boardOrientation}
                selectedSquare={selectedSquare}
                legalMoves={legalMoves}
                onSquareClick={handleSquareClick}
                lastMove={displayedLastMove}
                suggestedMove={hoveredCoachMove || displayedSuggestedMove}
                previewMove={hoveredCoachMove}
                renderMoves={displayedMoves}
              />
            </div>

            <div className="flex h-10 w-full items-center justify-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleStepBackward}
                disabled={!canStepBackward}
                className="h-8 w-12 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 disabled:border-white/10 disabled:text-white/25"
                data-testid="history-prev-btn"
              >
                <ChevronLeft size={16} />
              </Button>
              <div className="min-w-[132px] text-center font-mono text-[11px] uppercase tracking-[0.18em] text-white/45">
                {isReviewingHistory ? `Move ${reviewMoveIndex + 1}` : 'Live Position'}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleStepForward}
                disabled={!canStepForward}
                className="h-8 w-12 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/10 disabled:border-white/10 disabled:text-white/25"
                data-testid="history-next-btn"
              >
                <ChevronRight size={16} />
              </Button>
            </div>

            <div className="flex h-8 w-full items-center justify-center">
              {game.status !== 'active' ? (
                <div className="rounded-sm bg-white/10 px-6 py-2">
                  <span className="text-white font-bold uppercase">
                    {game.status === 'checkmate' ? 'Checkmate!' : game.status}
                  </span>
                </div>
              ) : (
                <span className="opacity-0">Game status</span>
              )}
            </div>

            <div className="flex h-10 w-full flex-wrap items-center justify-center gap-2 rounded-md border border-white/8 bg-white/[0.02] px-3">
              <Button variant="outline" size="sm" onClick={handleUndo} disabled={game.moves.length === 0}
                className="border-white/10 text-white/60 hover:bg-white/5 text-xs h-8" data-testid="undo-btn">
                <RotateCcw size={12} className="mr-1" /> Undo
              </Button>
              <Button variant="outline" size="sm" onClick={handleRefreshBestMove}
                disabled={!coachEnabled || isThinking}
                className="border-white/10 text-white/60 hover:bg-white/5 text-xs h-8" data-testid="suggest-btn">
                <Zap size={12} className="mr-1" /> Best Move
              </Button>
              <Button variant="outline" size="sm" onClick={handleShowTopLines}
                disabled={!coachEnabled || isThinking}
                className="border-white/10 text-white/60 hover:bg-white/5 text-xs h-8">
                <Swords size={12} className="mr-1" /> Top Lines
              </Button>
              <Button variant="outline" size="sm" onClick={handleExportPGN}
                className="border-white/10 text-white/60 hover:bg-white/5 text-xs h-8" data-testid="export-btn">
                <Download size={12} className="mr-1" /> PGN
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowDeleteDialog(true)}
                className="border-red-900/50 text-red-400/70 hover:bg-red-900/20 text-xs h-8" data-testid="delete-btn">
                <Trash2 size={12} className="mr-1" /> Delete
              </Button>
            </div>
          </div>

          <div className="order-3 flex flex-col gap-3">
            <div className="h-[248px] overflow-hidden rounded-md border border-amber-500/20 bg-amber-500/5 p-3">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-300/80">
                <Sparkles size={12} />
                Current Recommendation
              </div>
              {displayedBestMoveData && displayedSuggestedMove ? (
                <div className="mt-3 h-[188px] space-y-3 overflow-y-auto text-xs text-white/80">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-sm bg-amber-500/10 px-2 py-1 font-mono text-amber-300">{displayedBestMoveData.san}</span>
                    <span className="font-mono text-white/45">{displayedSuggestedMove}</span>
                    <span className="font-mono text-emerald-300">{displayedTopLines[0]?.eval || '--'}</span>
                  </div>
                  <p className="leading-relaxed text-white/70">{displayedBestMoveData.idea}</p>
                  {displayedBestMoveData.pv_san?.length ? (
                    <button
                      type="button"
                      onMouseEnter={() => setHoveredCoachMove(displayedSuggestedMove)}
                      onMouseLeave={() => setHoveredCoachMove(null)}
                      onFocus={() => setHoveredCoachMove(displayedSuggestedMove)}
                      onBlur={() => setHoveredCoachMove(null)}
                      className="w-full rounded border border-white/10 bg-white/5 px-3 py-2 text-left font-mono text-[11px] text-emerald-200 hover:bg-white/10"
                    >
                      {displayedBestMoveData.pv_san.slice(0, 6).join(' ')}
                    </button>
                  ) : null}
                </div>
              ) : (
                <p className="mt-3 text-xs leading-relaxed text-white/45">
                  {game.is_player_turn ? 'The engine will keep this slot updated with the best move for the live position.' : "Enter your opponent's move to refresh the recommendation."}
                </p>
              )}
            </div>

            <div className="h-[336px] overflow-hidden rounded-md border border-white/5 bg-white/[0.02] p-3">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/55">
                <Swords size={12} />
                Top Lines
              </div>
              {displayedTopLines.length ? (
                <div className="mt-3 h-[276px] space-y-2 overflow-y-auto">
                  {displayedTopLines.map((line) => (
                    <button
                      key={line.rank + '-' + line.uci}
                      type="button"
                      onMouseEnter={() => setHoveredCoachMove(line.uci)}
                      onMouseLeave={() => setHoveredCoachMove(null)}
                      onFocus={() => setHoveredCoachMove(line.uci)}
                      onBlur={() => setHoveredCoachMove(null)}
                      className="w-full rounded border border-white/8 bg-black/20 px-3 py-2 text-left hover:border-emerald-500/30 hover:bg-black/30"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-mono text-xs text-white">#{line.rank} {line.san}</span>
                        <span className="font-mono text-[11px] text-emerald-300">{line.eval}</span>
                      </div>
                      <p className="mt-1 font-mono text-[11px] leading-relaxed text-white/45">
                        {line.pv_san?.slice(0, 6).join(' ')}
                      </p>
                    </button>
                  ))}
                </div>
              ) : (
                <p className="mt-3 text-xs text-white/35">Request a refresh to load the current principal variations.</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Right - Engine Log */}
      <aside className="order-3 w-full lg:w-[360px] border-t lg:border-t-0 lg:border-l border-white/5 bg-[#0A0A0A] flex flex-col min-h-[320px] h-[48vh] lg:h-[calc(100vh-96px)]">
        <div className="border-b border-white/5 p-3">
          <div className="flex items-center gap-2">
            <Bot size={16} className="text-amber-400" />
            <span className="font-heading text-sm font-semibold text-white">Engine Log</span>
          </div>
          <p className="mt-1 text-[10px] text-white/40">
            Playing as <span className="text-amber-400">{game.player_color}</span> vs {game.opponent_name}
          </p>
        </div>

        <ScrollArea className="flex-1 min-h-0 p-3">
          <div ref={chatViewportRef}>
          <div className="space-y-3">
            {chatMessages.length > 0 ? (
              <div className="rounded-md border border-white/5 bg-white/[0.02] p-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/55">Conversation</div>
                <div className="mt-3 space-y-3">
                  {chatMessages.map((msg, idx) => (
                    <div key={idx} className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : ''}`}>
                      {msg.role === 'assistant' && (
                        <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-amber-500/20">
                          <Bot size={10} className="text-amber-400" />
                        </div>
                      )}
                      <div className={`max-w-[90%] rounded px-3 py-2 text-xs leading-relaxed break-words ${
                        msg.role === 'user'
                          ? 'bg-sky-900/30 text-sky-100'
                          : 'bg-white/5 text-white/80'
                      }`}>
                        {msg.role === 'assistant' ? (
                          <ReactMarkdown
                            components={{
                              strong: ({children}) => <span className="font-bold text-amber-400">{children}</span>,
                              p: ({children}) => <p className="mb-1 last:mb-0">{children}</p>,
                              a: ({ href, children }) => {
                                if (typeof href === 'string' && href.startsWith('#move-')) {
                                  const move = href.slice(6);
                                  return (
                                    <button
                                      type="button"
                                      onClick={(e) => e.preventDefault()}
                                      onMouseEnter={() => setHoveredCoachMove(move)}
                                      onMouseLeave={() => setHoveredCoachMove(null)}
                                      onFocus={() => setHoveredCoachMove(move)}
                                      onBlur={() => setHoveredCoachMove(null)}
                                      className="font-mono text-emerald-300 underline decoration-emerald-500/50 underline-offset-2 hover:text-emerald-200"
                                      title={`Preview move ${move}`}
                                    >
                                      {children}
                                    </button>
                                  );
                                }
                                return (
                                  <a href={href} target="_blank" rel="noopener noreferrer" className="underline">
                                    {children}
                                  </a>
                                );
                              },
                              code: ({children}) => (
                                <code className="rounded bg-emerald-500/10 px-1 py-0.5 font-mono text-emerald-300">
                                  {children}
                                </code>
                              ),
                            }}
                          >
                            {decorateCoachMarkdown(msg.content, displayedPosition)}
                          </ReactMarkdown>
                        ) : (
                          msg.content
                        )}
                      </div>
                      {msg.role === 'user' && (
                        <div className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-sky-500/20">
                          <User size={10} className="text-sky-400" />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="rounded-md border border-white/5 bg-white/[0.02] p-3 text-xs text-white/35">
                Engine messages will collect here as the game develops.
              </div>
            )}

            {isThinking ? (
              <div className="flex gap-2 rounded-md border border-white/5 bg-white/[0.02] p-3">
                <div className="flex h-5 w-5 items-center justify-center rounded-full bg-amber-500/20">
                  <Bot size={10} className="text-amber-400" />
                </div>
                <div className="flex gap-1 self-center">
                  <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/40" />
                  <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/40" style={{animationDelay: '0.1s'}} />
                  <div className="h-1.5 w-1.5 animate-bounce rounded-full bg-white/40" style={{animationDelay: '0.2s'}} />
                </div>
              </div>
            ) : null}
            <div ref={chatBottomRef} />
          </div>
          </div>
        </ScrollArea>

        <div className="border-t border-white/5 p-3">
          <p className="mb-2 text-[10px] text-white/35">
            Stockfish decides the move. The text layer only exposes engine output and lines.
          </p>
          <div className="flex gap-2">
            <Input
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleChatSend()}
              placeholder={engineUnavailable ? 'Engine unavailable' : 'Ask for best move, top lines, or a forcing plan'}
              disabled={!coachEnabled || isThinking}
              className="h-8 min-w-0 border-white/10 bg-white/5 text-xs text-white"
              data-testid="chat-input"
            />
            <Button onClick={handleChatSend} disabled={!coachEnabled || isThinking || !chatInput.trim()}
              className="h-8 bg-amber-500 px-3 text-black hover:bg-amber-600" data-testid="chat-send">
              <Send size={12} />
            </Button>
          </div>
          <div className="mt-2 flex flex-wrap gap-1">
            {['Best move?', 'Show the top three engine lines for this position.', 'Find the most forcing line for me.'].map(q => (
              <button key={q} onClick={() => setChatInput(q)} disabled={!coachEnabled}
                className="rounded border border-white/10 bg-white/5 px-2 py-1 text-[9px] text-white/40 hover:text-white/70 disabled:opacity-30">
                {q}
              </button>
            ))}
          </div>
        </div>
      </aside>

      {/* Delete Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-[#0A0A0A] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Game?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 text-white/70">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-900/50 text-red-400">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

